import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  ChopAlterskategorie,
  ChopKatalog,
  ChopLeistungsgruppe,
  ChopMassnahme,
  ChopMesskriterium,
  ChopMesskriteriumMassnahmenRegel,
  ChopProfil,
  ChopProfilMassnahme,
  ChopPunktekategorie,
} from '@entity/chop';

@Injectable({ providedIn: 'root' })
export class ChopService {
  private httpClient = inject(HttpClient);

  private readonly baseUrl = 'api/1.0/chop';

  // ChopKatalog
  getKataloge(): Observable<ChopKatalog[]> {
    return this.httpClient.get<ChopKatalog[]>(`${this.baseUrl}/kataloge`);
  }

  getKatalog(id: number): Observable<ChopKatalog> {
    return this.httpClient.get<ChopKatalog>(`${this.baseUrl}/kataloge/${id}`);
  }

  createKatalog(katalog: ChopKatalog): Observable<ChopKatalog> {
    return this.httpClient.post<ChopKatalog>(`${this.baseUrl}/kataloge`, katalog);
  }

  updateKatalog(id: number, katalog: ChopKatalog): Observable<ChopKatalog> {
    return this.httpClient.put<ChopKatalog>(`${this.baseUrl}/kataloge/${id}`, katalog);
  }

  deleteKatalog(id: number): Observable<unknown> {
    return this.httpClient.delete(`${this.baseUrl}/kataloge/${id}`);
  }

  // ChopAlterskategorie
  getAlterskategorien(katalogId: number): Observable<ChopAlterskategorie[]> {
    return this.httpClient.get<ChopAlterskategorie[]>(`${this.baseUrl}/kataloge/${katalogId}/alterskategorien`);
  }

  getAlterskategorie(id: number): Observable<ChopAlterskategorie> {
    return this.httpClient.get<ChopAlterskategorie>(`${this.baseUrl}/alterskategorien/${id}`);
  }

  createAlterskategorie(katalogId: number, value: ChopAlterskategorie): Observable<ChopAlterskategorie> {
    return this.httpClient.post<ChopAlterskategorie>(`${this.baseUrl}/kataloge/${katalogId}/alterskategorien`, {
      ...value,
    });
  }

  updateAlterskategorie(id: number, value: ChopAlterskategorie): Observable<ChopAlterskategorie> {
    return this.httpClient.put<ChopAlterskategorie>(`${this.baseUrl}/alterskategorien/${id}`, value);
  }

  deleteAlterskategorie(id: number): Observable<unknown> {
    return this.httpClient.delete(`${this.baseUrl}/alterskategorien/${id}`);
  }

  // ChopLeistungsgruppe
  getLeistungsgruppen(alterskategorieId: number): Observable<ChopLeistungsgruppe[]> {
    return this.httpClient.get<ChopLeistungsgruppe[]>(
      `${this.baseUrl}/alterskategorien/${alterskategorieId}/leistungsgruppen`
    );
  }

  getLeistungsgruppe(id: number): Observable<ChopLeistungsgruppe> {
    return this.httpClient.get<ChopLeistungsgruppe>(`${this.baseUrl}/leistungsgruppen/${id}`);
  }

  createLeistungsgruppe(alterskategorieId: number, value: ChopLeistungsgruppe): Observable<ChopLeistungsgruppe> {
    return this.httpClient.post<ChopLeistungsgruppe>(
      `${this.baseUrl}/alterskategorien/${alterskategorieId}/leistungsgruppen`,
      value
    );
  }

  updateLeistungsgruppe(id: number, value: ChopLeistungsgruppe): Observable<ChopLeistungsgruppe> {
    return this.httpClient.put<ChopLeistungsgruppe>(`${this.baseUrl}/leistungsgruppen/${id}`, value);
  }

  deleteLeistungsgruppe(id: number): Observable<unknown> {
    return this.httpClient.delete(`${this.baseUrl}/leistungsgruppen/${id}`);
  }

  // ChopPunktekategorie
  getPunktekategorien(alterskategorieId: number): Observable<ChopPunktekategorie[]> {
    return this.httpClient.get<ChopPunktekategorie[]>(
      `${this.baseUrl}/alterskategorien/${alterskategorieId}/punktekategorien`
    );
  }

  createPunktekategorie(alterskategorieId: number, value: ChopPunktekategorie): Observable<ChopPunktekategorie> {
    return this.httpClient.post<ChopPunktekategorie>(
      `${this.baseUrl}/alterskategorien/${alterskategorieId}/punktekategorien`,
      value
    );
  }

  updatePunktekategorie(id: number, value: ChopPunktekategorie): Observable<ChopPunktekategorie> {
    return this.httpClient.put<ChopPunktekategorie>(`${this.baseUrl}/punktekategorien/${id}`, value);
  }

  deletePunktekategorie(id: number): Observable<unknown> {
    return this.httpClient.delete(`${this.baseUrl}/punktekategorien/${id}`);
  }

  // ChopProfil
  getProfile(leistungsgruppeId: number): Observable<ChopProfil[]> {
    return this.httpClient.get<ChopProfil[]>(`${this.baseUrl}/leistungsgruppen/${leistungsgruppeId}/profile`);
  }

  getProfil(id: number): Observable<ChopProfil> {
    return this.httpClient.get<ChopProfil>(`${this.baseUrl}/profile/${id}`);
  }

  createProfil(leistungsgruppeId: number, value: ChopProfil): Observable<ChopProfil> {
    return this.httpClient.post<ChopProfil>(`${this.baseUrl}/leistungsgruppen/${leistungsgruppeId}/profile`, value);
  }

  updateProfil(id: number, value: ChopProfil): Observable<ChopProfil> {
    return this.httpClient.put<ChopProfil>(`${this.baseUrl}/profile/${id}`, value);
  }

  deleteProfil(id: number): Observable<unknown> {
    return this.httpClient.delete(`${this.baseUrl}/profile/${id}`);
  }

  // ChopMesskriterium
  getMesskriterien(profilId: number): Observable<ChopMesskriterium[]> {
    return this.httpClient.get<ChopMesskriterium[]>(`${this.baseUrl}/profile/${profilId}/messkriterien`);
  }

  getMesskriterium(id: number): Observable<ChopMesskriterium> {
    return this.httpClient.get<ChopMesskriterium>(`${this.baseUrl}/messkriterien/${id}`);
  }

  createMesskriterium(profilId: number, value: ChopMesskriterium): Observable<ChopMesskriterium> {
    return this.httpClient.post<ChopMesskriterium>(`${this.baseUrl}/profile/${profilId}/messkriterien`, value);
  }

  updateMesskriterium(id: number, value: ChopMesskriterium): Observable<ChopMesskriterium> {
    return this.httpClient.put<ChopMesskriterium>(`${this.baseUrl}/messkriterien/${id}`, value);
  }

  deleteMesskriterium(id: number): Observable<unknown> {
    return this.httpClient.delete(`${this.baseUrl}/messkriterien/${id}`);
  }

  // ChopMesskriteriumMassnahmenRegel
  getRegeln(messkriteriumId: number): Observable<ChopMesskriteriumMassnahmenRegel[]> {
    return this.httpClient.get<ChopMesskriteriumMassnahmenRegel[]>(
      `${this.baseUrl}/messkriterien/${messkriteriumId}/regeln`
    );
  }

  createRegel(
    messkriteriumId: number,
    value: ChopMesskriteriumMassnahmenRegel
  ): Observable<ChopMesskriteriumMassnahmenRegel> {
    return this.httpClient.post<ChopMesskriteriumMassnahmenRegel>(
      `${this.baseUrl}/messkriterien/${messkriteriumId}/regeln`,
      value
    );
  }

  updateRegel(id: number, value: ChopMesskriteriumMassnahmenRegel): Observable<ChopMesskriteriumMassnahmenRegel> {
    return this.httpClient.put<ChopMesskriteriumMassnahmenRegel>(`${this.baseUrl}/regeln/${id}`, value);
  }

  deleteRegel(id: number): Observable<unknown> {
    return this.httpClient.delete(`${this.baseUrl}/regeln/${id}`);
  }

  // Massnahmen Picker
  getMassnahmen(jahr: number): Observable<ChopMassnahme[]> {
    return this.httpClient.get<ChopMassnahme[]>(`${this.baseUrl}/massnahmen`, { params: { jahr } });
  }

  // ChopProfilMassnahme
  getProfilMassnahmen(profilId: number): Observable<ChopProfilMassnahme[]> {
    return this.httpClient.get<ChopProfilMassnahme[]>(`${this.baseUrl}/profile/${profilId}/massnahmen`);
  }

  addProfilMassnahme(profilId: number, tacsCodeKreisId: number): Observable<ChopProfilMassnahme> {
    return this.httpClient.post<ChopProfilMassnahme>(`${this.baseUrl}/profile/${profilId}/massnahmen`, {
      tacsCodeKreisId,
    });
  }

  removeProfilMassnahme(profilId: number, tacsCodeKreisId: number): Observable<unknown> {
    return this.httpClient.delete(`${this.baseUrl}/profile/${profilId}/massnahmen/${tacsCodeKreisId}`);
  }
}
